import { MAX_DIMENSION_ATTEMPTS } from './publicDiagrams/constants.js';
import { buildDiagramDefinition } from './publicDiagrams/definitions.js';
import {
  findNodeAtEvent,
  hideOverlay,
  parseDiagramData,
  updateOverlay
} from './publicDiagrams/dom.js';
import { drawDiagram } from './publicDiagrams/drawing.js';
import { measureCanvas } from './publicDiagrams/layout.js';

export { buildDiagramDefinition } from './publicDiagrams/definitions.js';

export function initPublicDiagrams(root = document) {
  const canvases = root.querySelectorAll('canvas[data-public-diagram-canvas]');

  canvases.forEach(canvas => {
    if (canvas.dataset.publicDiagramInitialized === 'true') return;
    if (canvas.dataset.publicDiagramInitializing === 'true') return;

    const renderer = renderPublicDiagram({
      canvas,
      type: canvas.dataset.publicDiagramType,
      data: parseDiagramData(canvas)
    });

    if (!renderer) return;

    canvas.dataset.publicDiagramInitializing = 'true';
    canvas.publicDiagramRenderer = renderer;
  });
}

export function renderPublicDiagram({ canvas, type, data = {} }) {
  const ctx = canvas.getContext?.('2d');
  const wrapper = canvas.parentElement;
  if (!ctx || !wrapper) return null;

  const definition = buildDiagramDefinition(type, data);
  const overlay = wrapper.querySelector('[data-public-diagram-overlay]');
  const overlayPhase = wrapper.querySelector('[data-public-diagram-overlay-phase]');
  const overlayTitle = wrapper.querySelector('[data-public-diagram-overlay-title]');
  const overlayDescription = wrapper.querySelector('[data-public-diagram-overlay-description]');
  const state = {
    frameId: null,
    resizeHandler: null,
    hoverNode: null,
    attempts: 0,
    dimensions: { width: 0, height: 0 },
    displayNodes: []
  };

  const drawWhenReady = () => {
    const dimensions = measureCanvas(wrapper, canvas);

    if (!dimensions.measured && state.attempts < MAX_DIMENSION_ATTEMPTS) {
      state.attempts += 1;
      state.frameId = window.requestAnimationFrame?.(drawWhenReady) || null;
      return;
    }

    drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions });
    canvas.dataset.publicDiagramInitialized = 'true';
    canvas.dataset.publicDiagramInitializing = 'false';

    if (!state.resizeHandler) {
      state.resizeHandler = () => drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions: measureCanvas(wrapper, canvas) });
      window.addEventListener('resize', state.resizeHandler);
    }
  };

  const handleMouseMove = event => {
    if (state.displayNodes.length === 0) return;

    const found = findNodeAtEvent(canvas, state.displayNodes, event);

    if (found === state.hoverNode) {
      if (found) updateOverlay({ canvas, overlay, overlayPhase, overlayTitle, overlayDescription, event, node: found });
      return;
    }
    state.hoverNode = found || null;
    updateOverlay({ canvas, overlay, overlayPhase, overlayTitle, overlayDescription, event, node: state.hoverNode });
    drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions: state.dimensions });
  };

  const handleMouseLeave = () => {
    state.hoverNode = null;
    hideOverlay(overlay);
    drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions: state.dimensions });
  };

  const nodeCenterEvent = node => {
    const rect = canvas.getBoundingClientRect();
    return { clientX: rect.left + node.left + (node.width / 2), clientY: rect.top + node.top + node.height };
  };

  const selectNode = node => {
    state.hoverNode = node || null;
    if (node) {
      updateOverlay({ canvas, overlay, overlayPhase, overlayTitle, overlayDescription, event: nodeCenterEvent(node), node });
    } else {
      hideOverlay(overlay);
    }
    drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions: state.dimensions });
  };

  const handleKeyDown = event => {
    if (state.displayNodes.length === 0) return;

    const currentIndex = state.displayNodes.findIndex(node => node.id === state.hoverNode?.id);
    const lastIndex = state.displayNodes.length - 1;
    const nextIndex = {
      ArrowRight: currentIndex < lastIndex ? currentIndex + 1 : 0,
      ArrowDown: currentIndex < lastIndex ? currentIndex + 1 : 0,
      ArrowLeft: currentIndex > 0 ? currentIndex - 1 : lastIndex,
      ArrowUp: currentIndex > 0 ? currentIndex - 1 : lastIndex,
      Home: 0,
      End: lastIndex
    }[event.key];

    if (event.key === 'Escape') {
      selectNode(null);
      return;
    }
    if (nextIndex === undefined) return;

    event.preventDefault();
    selectNode(state.displayNodes[nextIndex]);
  };

  const handleFocus = () => {
    if (!state.hoverNode && state.displayNodes.length > 0) selectNode(state.displayNodes[0]);
  };

  const handlePointerDown = event => {
    if (state.displayNodes.length === 0) return;

    const found = findNodeAtEvent(canvas, state.displayNodes, event);
    state.hoverNode = found || null;
    updateOverlay({ canvas, overlay, overlayPhase, overlayTitle, overlayDescription, event, node: state.hoverNode });
    drawDiagram({ canvas, ctx, wrapper, definition, state, dimensions: state.dimensions });
  };

  canvas.addEventListener('mousemove', handleMouseMove);
  canvas.addEventListener('mouseleave', handleMouseLeave);
  canvas.addEventListener('pointerdown', handlePointerDown);
  canvas.addEventListener('keydown', handleKeyDown);
  canvas.addEventListener('focus', handleFocus);
  canvas.addEventListener('blur', handleMouseLeave);
  renderAccessibleNodeList(wrapper, definition);
  state.frameId = window.requestAnimationFrame?.(drawWhenReady) || null;

  return {
    destroy() {
      if (state.frameId) window.cancelAnimationFrame?.(state.frameId);
      if (state.resizeHandler) window.removeEventListener('resize', state.resizeHandler);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('focus', handleFocus);
      canvas.removeEventListener('blur', handleMouseLeave);
    }
  };
}

// Canvas content is invisible to assistive tech, so mirror every node as a screen-reader-only list.
function renderAccessibleNodeList(wrapper, definition) {
  if (wrapper.querySelector('[data-public-diagram-text]')) return;

  const list = document.createElement('ol');
  list.className = 'sr-only';
  list.dataset.publicDiagramText = 'true';
  definition.nodes.forEach(node => {
    const item = document.createElement('li');
    item.textContent = [`${node.phase}: ${node.label}`, node.description].filter(Boolean).join('. ');
    list.appendChild(item);
  });
  wrapper.appendChild(list);
}
